import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../carbon/DataTable/DataTable";
import EmptyState from "../carbon/EmptyState/EmptyState";
import { formatBiddingDateTime } from "../../utils";
import { SimpleOrderPaginatedResponse } from "../../types";
import "./ordersTable.scss";

type OrdersTableProps = {
  orders: SimpleOrderPaginatedResponse;
  title?: string;
  pageNumber: number;
  setPageNumber: (page: number) => void;
};

interface OrderRow extends DataTableRow {
  id: string;
  internalCode: string;
  name: string;
  volume: string;
  start: string;
  end: string;
}

/**
 * The orders/RFQ list table — rebuilt on the shared Carbon `DataTable`, replacing the pre-Carbon
 * Tailwind `<table>` + `OrdersTableRow`. The whole row links to `/orders/{id}` (via `onRowClick`),
 * the same single destination the pre-Carbon row's name/code/chevron links all pointed at.
 *
 * Consumed unchanged by every screen that showed this table (the Orders sub-tabs, the Home
 * dashboards, BiddingOpportunities and the material-detail RFQs tab) — the props are identical, so
 * this conversion updates all of them at once.
 */
const OrdersTable = ({
  orders,
  title,
  pageNumber,
  setPageNumber,
}: OrdersTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const headers: DataTableColumn<OrderRow>[] = [
    { key: "internalCode", header: t("tenders.form.internalCode") },
    { key: "name", header: t("tenders.form.name") },
    { key: "volume", header: t("tenders.tenderSummary.volume") },
    { key: "start", header: t("tenders.tenderSummary.start") },
    { key: "end", header: t("tenders.tenderSummary.end") },
  ];

  const rows: OrderRow[] = (orders?.content ?? [])
    .slice()
    .sort((a, b) =>
      (a.material?.globalMaterialName ?? "").localeCompare(
        b.material?.globalMaterialName ?? ""
      )
    )
    .map((order) => ({
      id: order.id,
      internalCode: order?.internalCode || "-",
      name: order?.name ?? "",
      volume: `${order.quantity ?? ""} ${order.measurementUnit ?? ""}`.trim(),
      start: formatBiddingDateTime(order.opensAt, order.startDate),
      end: formatBiddingDateTime(order.closesAt, order.finishDate),
    }));

  return (
    <div className="orders-table">
      {title ? <h2 className="orders-table__title">{title}</h2> : null}
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        onRowClick={(row) => navigate(`/orders/${row.id}`)}
        pagination={{
          page: pageNumber + 1,
          pageSize: orders?.size ?? 10,
          totalItems: orders?.totalElements ?? 0,
          onPageChange: (newPage) => setPageNumber(newPage - 1),
        }}
        empty={<EmptyState title={t("order.noTender")} />}
      />
    </div>
  );
};

export default OrdersTable;
